import { z } from 'zod';
import { validators } from '@huspy/forge/shared';
import { t as i18n } from 'i18next';
import { BUYOUT_TRANSACTION_TYPES } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage';
import { convertYearsAndMonthsToTotalMonths } from '@shared/utils/date-utils';

const requiredFieldError = i18n('common.validation.requiredField');
const { textFieldValidator, numberCoerceFieldValidator } = validators;

const validateMaxMortgageLengthMonths = (data: { mortgage_details: { years?: number | null, months?: number | null } }) => {
  const years = Number(data.mortgage_details.years);
  const months = Number(data.mortgage_details.months);

  if (Number.isNaN(years) || Number.isNaN(months)) {
    return true; // let field-level validators handle invalid numbers
  }

  const totalMonths = convertYearsAndMonthsToTotalMonths(
    years,
    months
  );
  return totalMonths <= 300;
};

const validateMinMortgageLengthMonths = (data: { mortgage_details: { years?: number | null, months?: number | null } }) => {
  const years = Number(data.mortgage_details.years);
  const months = Number(data.mortgage_details.months);

  if (Number.isNaN(years) || Number.isNaN(months)) {
    return true; // let field-level validators handle invalid numbers
  }

  const totalMonths = convertYearsAndMonthsToTotalMonths(
    years,
    months
  );
  return totalMonths >= 1;
};

export const clientAndMortgageUAESchema = z.object({
  client_details: z.object({
    first_name: textFieldValidator,
    last_name: textFieldValidator,
    employment_status: textFieldValidator,
    citizen_status: textFieldValidator,
  }),
  property_details: z.object({
    state: textFieldValidator,
    is_difc: textFieldValidator,
    property_location: textFieldValidator.optional().or(z.literal('')),
    property_status: textFieldValidator,
  }),
  mortgage_details: z.object({
    transaction_type: textFieldValidator,
    mortgage_type: textFieldValidator,
    existing_mortgage_type: textFieldValidator.optional().or(z.literal('')),
    property_value: numberCoerceFieldValidator.min(1, { message: requiredFieldError }).nonnegative(),
    loan_value: numberCoerceFieldValidator.min(1, { message: requiredFieldError }).nonnegative(),
    ltv: numberCoerceFieldValidator.min(1, { message: requiredFieldError }).nonnegative().max(
      100,
      { message: 'LTV cannot be greater than 100%' }
    ),
    down_payment: numberCoerceFieldValidator.nonnegative().max(
      100,
      { message: 'Down payment % cannot be greater than 100%' }
    ),
    downpayment_amount: numberCoerceFieldValidator.nonnegative(),
    equity_release_amount: numberCoerceFieldValidator.optional(),
    outstanding_loan_amount: numberCoerceFieldValidator.optional(),
    years: numberCoerceFieldValidator.min(0).max(25).nullish(),
    months: numberCoerceFieldValidator.min(0).max(12).nullish(),
    is_real_estate_fee_financed: z.boolean().optional(),
    is_land_department_fee_financed: z.boolean().optional(),
  }),
}).refine((data) => {
  const isAlreadyHaveMortgage = BUYOUT_TRANSACTION_TYPES.includes(data.mortgage_details.transaction_type);

  if (!isAlreadyHaveMortgage) {
    return data.mortgage_details.down_payment && data.mortgage_details.downpayment_amount;
  }

  return !(isAlreadyHaveMortgage && !data.mortgage_details.existing_mortgage_type);
}, {
  message: requiredFieldError,
  path: ['mortgage_details.existing_mortgage_type'],
}).refine(
  (data) => data.property_details.state === 'dubai' || data.property_details.property_location !== 'DIFC',
  {
    message: 'Location cannot be DIFC if state is not Dubai',
    path: ['property_details.property_location'],
  }
).refine(
  (data) => {
    const propertyValue = Number(data.mortgage_details.property_value);
    const downpaymentAmount = Number(data.mortgage_details.downpayment_amount);

    if (propertyValue && downpaymentAmount) {
      return downpaymentAmount < propertyValue;
    }

    return true;
  },
  {
    message: 'Down payment amount must be less than property value',
    path: ['mortgage_details.downpayment_amount'],
  }
)
  .refine(
    (data) => {
      const propertyValue = Number(data.mortgage_details.property_value);
      const loanValue = Number(data.mortgage_details.loan_value);

      if (propertyValue && loanValue) {
        return loanValue < propertyValue;
      }

      return true;
    },
    {
      message: 'Loan amount must be less than property value',
      path: ['mortgage_details.loan_value'],
    }
  )
  .refine(
    validateMinMortgageLengthMonths,
    {
      message: 'Mortgage length must be at least 1 month',
      path: ['mortgage_details.years'],
    }
  )
  .refine(
    validateMinMortgageLengthMonths,
    {
      message: 'Mortgage length must be at least 1 month',
      path: ['mortgage_details.months'],
    }
  )
  .refine(
    validateMaxMortgageLengthMonths,
    {
      message: 'Mortgage length must be maximum 25 years',
      path: ['mortgage_details.years'],
    }
  )
  .refine(
    validateMaxMortgageLengthMonths,
    {
      message: 'Mortgage length must be maximum 25 years',
      path: ['mortgage_details.months'],
    }
  );
